import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronRight, Copy, Download, FileCode, FileJson, FileText, Folder, Search, WrapText, X } from 'lucide-react';
import { highlightLines, langLabel, langOf, tokenClass, type Lang } from './codeHighlight';

export interface ViewerFile {
  path: string;
  content: string;
}

interface FileViewerModalProps {
  files: ViewerFile[];
  selectedPath: string;
  onSelect: (path: string) => void;
  onClose: () => void;
}

function formatSize(n: number) {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

const langBadge: Record<Lang, string> = {
  ts: 'text-sky-300 bg-sky-500/10',
  json: 'text-amber-300 bg-amber-500/10',
  css: 'text-pink-300 bg-pink-500/10',
  html: 'text-orange-300 bg-orange-500/10',
  md: 'text-gray-300 bg-white/5',
  text: 'text-gray-400 bg-white/5',
};

function FileIcon({ path, className }: { path: string; className?: string }) {
  const lang = langOf(path);
  if (lang === 'json') return <FileJson className={className} />;
  if (lang === 'ts' || lang === 'css' || lang === 'html') return <FileCode className={className} />;
  return <FileText className={className} />;
}

/** Group files by directory ("" = project root), dirs sorted with root first. */
function groupByDir(files: ViewerFile[]) {
  const groups = new Map<string, ViewerFile[]>();
  for (const f of files) {
    const i = f.path.lastIndexOf('/');
    const dir = i === -1 ? '' : f.path.slice(0, i);
    groups.set(dir, [...(groups.get(dir) ?? []), f]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    .map(([dir, fs]) => [dir, fs.sort((x, y) => x.path.localeCompare(y.path))] as const);
}

/**
 * Code viewer for generated deliverables: searchable folder-grouped file tree,
 * syntax-highlighted source with line numbers, wrap toggle, click-to-highlight
 * lines, copy/download, and keyboard navigation (Esc, ↑/↓ between files).
 */
export function FileViewerModal({ files, selectedPath, onSelect, onClose }: FileViewerModalProps) {
  const [copied, setCopied] = useState(false);
  const [wrap, setWrap] = useState(false);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const codeRef = useRef<HTMLDivElement>(null);

  const ordered = useMemo(() => groupByDir(files).flatMap(([, fs]) => fs), [files]);
  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groupByDir(q ? files.filter((f) => f.path.toLowerCase().includes(q)) : files);
  }, [files, query]);

  const idx = ordered.findIndex((f) => f.path === selectedPath);
  const file = ordered[idx];
  const lang = langOf(selectedPath);
  const lines = useMemo(() => (file ? highlightLines(file.content, lang) : []), [file, lang]);
  const gutter = String(lines.length).length;

  useEffect(() => {
    setCopied(false);
    setActiveLine(null);
    codeRef.current?.scrollTo({ top: 0 });
  }, [selectedPath]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.tagName === 'INPUT';
      if (e.key === 'Escape') { if (typing && query) setQuery(''); else onClose(); return; }
      if (typing) return;
      if (e.key === 'ArrowDown' && idx < ordered.length - 1) { e.preventDefault(); onSelect(ordered[idx + 1].path); }
      if (e.key === 'ArrowUp' && idx > 0) { e.preventDefault(); onSelect(ordered[idx - 1].path); }
      if (e.key.toLowerCase() === 'w' && !e.metaKey && !e.ctrlKey) setWrap((w) => !w);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, ordered, onSelect, onClose, query]);

  const copy = async () => {
    if (!file) return;
    try {
      await navigator.clipboard.writeText(file.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const download = () => {
    if (!file) return;
    const url = URL.createObjectURL(new Blob([file.content], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = file.path.split('/').pop() || 'file.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const crumbs = selectedPath.split('/');

  // Lock page scroll behind the viewer.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Portal to <body>: rendered inside the page, an ancestor with a transform /
  // backdrop-filter becomes the containing block for `position: fixed`, so the
  // overlay only covered part of the screen and the app chrome showed through.
  return createPortal(
    <div className="fixed inset-0 bg-[#05060a]/85 z-[1000] flex items-center justify-center p-3 md:p-8 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        role="dialog"
        aria-label={`Viewing ${selectedPath}`}
        className="bg-[#10121a] w-full max-w-7xl h-[88vh] rounded-2xl border border-[#2a2e3d] flex overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* File tree */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[#232736] bg-[#0c0e14]">
          <div className="px-3 pt-3 pb-2 border-b border-[#232736]">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Explorer</span>
              <span className="text-[11px] text-gray-600">{files.length} files</span>
            </div>
            <label className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[#151823] border border-[#232736] focus-within:border-primary-500/60">
              <Search className="w-3.5 h-3.5 text-gray-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter files…"
                className="flex-1 bg-transparent text-xs text-gray-200 placeholder-gray-600 outline-none"
              />
            </label>
          </div>
          <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
            {visibleGroups.length === 0 && <div className="px-4 py-3 text-xs text-gray-600">No files match “{query}”.</div>}
            {visibleGroups.map(([dir, fs]) => {
              const isCollapsed = !!collapsed[dir] && !query;
              return (
                <div key={dir || '(root)'} className="mb-1">
                  {dir && (
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [dir]: !c[dir] }))}
                      className="w-full flex items-center gap-1.5 px-3 py-1 text-[11px] text-gray-500 hover:text-gray-300"
                    >
                      {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      <Folder className="w-3.5 h-3.5 text-[#6f78a8]" />
                      <span className="font-mono truncate">{dir}</span>
                    </button>
                  )}
                  {!isCollapsed &&
                    fs.map((f) => {
                      const active = f.path === selectedPath;
                      return (
                        <button
                          key={f.path}
                          type="button"
                          onClick={() => onSelect(f.path)}
                          title={f.path}
                          className={`w-full flex items-center gap-2 py-1.5 pr-3 text-left text-xs transition-colors border-l-2 ${dir ? 'pl-8' : 'pl-3'} ${
                            active ? 'bg-primary-500/15 text-white border-primary-500' : 'text-gray-400 border-transparent hover:bg-[#161a26] hover:text-gray-200'
                          }`}
                        >
                          <FileIcon path={f.path} className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-primary-300' : 'text-[#7b82a8]'}`} />
                          <span className="truncate font-mono flex-1">{f.path.split('/').pop()}</span>
                          <span className="text-[10px] text-gray-600 tabular-nums">{formatSize(f.content.length)}</span>
                        </button>
                      );
                    })}
                </div>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-[#232736] text-[10px] text-gray-600 flex gap-3">
            <span><kbd className="font-mono">↑↓</kbd> switch</span>
            <span><kbd className="font-mono">W</kbd> wrap</span>
            <span><kbd className="font-mono">Esc</kbd> close</span>
          </div>
        </aside>

        {/* Source */}
        <section className="flex-1 min-w-0 flex flex-col">
          <header className="flex items-center gap-3 px-4 h-12 border-b border-[#232736] bg-[#0f1118]">
            <FileIcon path={selectedPath} className="w-4 h-4 text-primary-300 shrink-0" />
            <nav className="flex items-center gap-1 min-w-0 font-mono text-[13px]" aria-label="File path">
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1 min-w-0">
                  {i > 0 && <span className="text-gray-600">/</span>}
                  <span className={`truncate ${i === crumbs.length - 1 ? 'text-white font-semibold' : 'text-gray-500'}`}>{c}</span>
                </span>
              ))}
            </nav>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${langBadge[lang]}`}>{langLabel[lang]}</span>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setWrap((w) => !w)}
                aria-pressed={wrap}
                title="Toggle line wrap (W)"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${wrap ? 'bg-primary-500/20 text-primary-200' : 'text-gray-400 hover:bg-[#1f2330] hover:text-gray-200'}`}
              >
                <WrapText className="w-3.5 h-3.5" /> Wrap
              </button>
              <button type="button" onClick={copy} disabled={!file} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-400 hover:bg-[#1f2330] hover:text-gray-200 disabled:opacity-40">
                {copied ? <Check className="w-3.5 h-3.5 text-success-500" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" onClick={download} disabled={!file} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-400 hover:bg-[#1f2330] hover:text-gray-200 disabled:opacity-40">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <div className="w-px h-5 bg-[#232736] mx-1" />
              <button type="button" onClick={onClose} aria-label="Close (Esc)" title="Close (Esc)" className="p-1.5 rounded-lg text-gray-400 hover:bg-[#1f2330] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>

          {/* Mobile file picker */}
          <div className="md:hidden px-3 py-2 border-b border-[#232736] bg-[#0c0e14]">
            <select
              value={selectedPath}
              onChange={(e) => onSelect(e.target.value)}
              className="w-full bg-[#151823] border border-[#232736] rounded-lg px-2 py-1.5 text-xs text-gray-200 font-mono"
            >
              {ordered.map((f) => <option key={f.path} value={f.path}>{f.path}</option>)}
            </select>
          </div>

          <div ref={codeRef} className="flex-1 overflow-auto bg-[#0a0b10] custom-scrollbar">
            {file ? (
              <div className="py-3 font-mono text-[13px] leading-6 min-w-fit">
                {lines.map((toks, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveLine(i === activeLine ? null : i)}
                    className={`flex cursor-text ${i === activeLine ? 'bg-primary-500/10' : 'hover:bg-white/[0.025]'}`}
                  >
                    <span
                      className={`select-none sticky left-0 text-right pr-4 pl-4 tabular-nums shrink-0 bg-[#0a0b10] ${i === activeLine ? 'text-primary-300' : 'text-[#3b3f51]'}`}
                      style={{ width: `${gutter + 3}ch` }}
                    >
                      {i + 1}
                    </span>
                    <span className={`pr-6 ${wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'}`}>
                      {toks.length === 0 ? ' ' : toks.map((t, j) => <span key={j} className={tokenClass[t.kind]}>{t.text}</span>)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-sm text-gray-500">File content not found.</div>
            )}
          </div>

          <footer className="flex items-center gap-4 px-4 h-7 border-t border-[#232736] bg-[#0f1118] text-[11px] text-gray-500 font-mono">
            <span>{lines.length} lines</span>
            <span>{file ? formatSize(file.content.length) : '—'}</span>
            {activeLine !== null && <span className="text-primary-300">Ln {activeLine + 1}</span>}
            <span className="ml-auto">{idx + 1} / {ordered.length}</span>
          </footer>
        </section>
      </div>
    </div>
  , document.body);
}
